import React, { useState, useEffect } from 'react';
import {
  MdPeople, MdAdd, MdEdit, MdDelete, MdClose, MdSave, MdBlock, MdCheckCircle,
  MdFileDownload, MdPictureAsPdf, MdLockReset, MdContentCopy,
} from 'react-icons/md';
import api from '../api/axios';
import { downloadFromApi } from '../lib/fileDownload';
import { tsFilename } from '../lib/filename';
import { confirm } from '../lib/dialogs';
import SearchableSelect from '../components/SearchableSelect';

// Permission catalogue grouped semantically. Keep keys in sync with sidebar nav perms.
const PERMISSION_GROUPS = [
  {
    label: 'Core',
    items: [
      { key: 'dashboard',       label: 'Dashboard' },
      { key: 'asset-search',    label: 'Asset Search' },
    ],
  },
  {
    label: 'Asset Management',
    items: [
      { key: 'asset-registration', label: 'Asset Registration' },
      { key: 'all-assets',         label: 'All Assets' },
      { key: 'asset-movement',     label: 'Asset Movement' },
      { key: 'location-master',    label: 'Location Master' },
    ],
  },
  {
    label: 'Audit & Reporting',
    items: [
      { key: 'asset-audit',  label: 'Asset Audit' },
      { key: 'audit-report', label: 'Audit Report' },
      { key: 'reports',      label: 'Reports' },
    ],
  },
  {
    label: 'Scanning',
    items: [
      { key: 'raw-inventory',      label: 'Raw Inventory' },
      { key: 'handheld-simulator', label: 'Handheld Simulator' },
      { key: 'reader-manager',     label: 'Reader Manager' },
    ],
  },
  {
    label: 'Administration',
    items: [
      { key: 'user-management', label: 'User Management', adminOnly: true },
      { key: 'masters',         label: 'Masters',         adminOnly: true },
      { key: 'settings',        label: 'Settings',        adminOnly: true },
    ],
  },
  // Capability-level permissions. Admins always pass (hasPermission short-circuits
  // on role === 'admin'). For non-admin users these gate individual UI actions
  // (export buttons, import button, delete buttons, etc.) on top of the page-
  // level access above. Operator can have access to "Asset Audit" but not
  // be allowed to download the Excel/PDF report from it, for example.
  {
    label: 'Capabilities (per-action)',
    items: [
      { key: 'export-excel',         label: 'Export to Excel' },
      { key: 'export-pdf',           label: 'Export to PDF' },
      { key: 'import-data',          label: 'Import from Excel/CSV' },
      { key: 'delete-asset',         label: 'Delete assets' },
      { key: 'bulk-delete',          label: 'Bulk delete (multi-row)' },
      { key: 'clear-raw-inventory',  label: 'Clear raw inventory records' },
      { key: 'manual-tag-entry',     label: 'Type tag manually (Raw Inventory)' },
      // Grants Masters → Registration Templates → Edit → Fields, on the web and
      // on the handheld, without granting the rest of Masters administration.
      { key: 'template-fields-edit', label: 'Edit registration template fields' },
    ],
  },
];

const ALL_PERM_KEYS = PERMISSION_GROUPS.flatMap(g => g.items.map(p => p.key));

const initialForm = {
  full_name: '', username: '', email: '', password: '',
  role: 'operator', permissions: [], must_change_password: true,
  // Branch-manager scope. null = unrestricted (company-wide). When set,
  // user can only see/edit assets at this location and its descendants.
  scope_location_id: null,
};

const Toast = ({ msg, type, onClose }) => (
  <div className={`toast toast-${type}`} style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999 }}>
    {msg}
    <button onClick={onClose} style={{ marginLeft: 12, background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>×</button>
  </div>
);

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);   // for branch-scope dropdown
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [resetResult, setResetResult] = useState(null); // { username, temporary_password }
  const [form, setForm] = useState(initialForm);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleExport = async (type) => {
    try {
      const ext = type === 'excel' ? 'xlsx' : 'pdf';
      await downloadFromApi(`/users/export/${type}`, tsFilename('users', ext));
    } catch (err) {
      showToast(err.message || 'Export failed', 'error');
    }
  };

  const fetchUsers = async () => {
    const res = await api.get('/users');
    setUsers(res.data);
  };

  useEffect(() => { fetchUsers(); }, []);
  // Load locations once for the branch-scope dropdown. We catch silently
  // because operator users without location-master permission would 403,
  // and they shouldn't be on this page anyway (admin-only).
  useEffect(() => {
    api.get('/locations').then(r => setLocations(r.data || [])).catch(() => {});
  }, []);

  const handlePermToggle = (key, target) => {
    const current = target.permissions || [];
    const updated = current.includes(key)
      ? current.filter(p => p !== key)
      : [...current, key];
    return updated;
  };

  const handleAdd = async () => {
    if (!form.username || !form.password) { showToast('Username and password required', 'error'); return; }
    try {
      await api.post('/users', form);
      showToast('User created');
      setAddModal(false);
      setForm(initialForm);
      fetchUsers();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to create user', 'error');
    }
  };

  const handleEdit = async () => {
    try {
      await api.put(`/users/${editModal.id}`, editModal);
      showToast('User updated');
      setEditModal(null);
      fetchUsers();
    } catch (err) {
      showToast(err.response?.data?.error || 'Update failed', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/users/${deleteModal.id}`);
      showToast('User deleted');
      setDeleteModal(null);
      fetchUsers();
    } catch (err) {
      showToast(err.response?.data?.error || 'Delete failed', 'error');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await api.put(`/users/${user.id}`, { ...user, active: user.active ? 0 : 1 });
      showToast(`User ${user.active ? 'deactivated' : 'activated'}`);
      fetchUsers();
    } catch {
      showToast('Failed to update status', 'error');
    }
  };

  const handleResetPassword = async (user) => {
    if (!(await confirm(`Reset password for "${user.username}"? They'll be forced to change it on next login.`))) return;
    // Offer to email the temp password if the user has an address on file. We
    // don't force it because SMTP might not be configured — backend tells us
    // what happened and we surface it in the result modal.
    const emailIt = user.email
      ? await confirm(
          `Should we also email the new temporary password to ${user.email}? Requires SMTP to be configured. Cancel to show on-screen only.`,
          { title: 'Email temp password?', confirmLabel: 'Email it', cancelLabel: 'On-screen only' },
        )
      : false;
    try {
      const r = await api.post(`/users/${user.id}/reset-password`, { email_to_user: emailIt });
      setResetResult({
        username: r.data.username,
        temporary_password: r.data.temporary_password,
        email: r.data.email,
        user_email: r.data.user_email,
      });
      showToast(`Temporary password generated for ${user.username}`);
      fetchUsers();
    } catch (err) {
      showToast(err.response?.data?.error || 'Reset failed', 'error');
    }
  };

  const PermissionChecklist = ({ value, onChange, role }) => {
    const allKeysSelectable = PERMISSION_GROUPS.flatMap(g =>
      g.items.filter(p => !p.adminOnly || role === 'admin').map(p => p.key)
    );
    const selectAll = () => allKeysSelectable.forEach(k => { if (!value.includes(k)) onChange(k); });
    const clearAll  = () => value.forEach(k => onChange(k));

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 6 }}>
          <button type="button" className="btn btn-outline btn-sm" onClick={selectAll}>Select all</button>
          <button type="button" className="btn btn-outline btn-sm" onClick={clearAll}>Clear</button>
        </div>
        {PERMISSION_GROUPS.map(group => (
          <div key={group.label} style={{ marginBottom: 10 }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: '#666', textTransform: 'uppercase',
              letterSpacing: 0.5, marginBottom: 4, paddingBottom: 4, borderBottom: '1px solid #eee',
            }}>{group.label}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 4 }}>
              {group.items.map(p => {
                const disabled = p.adminOnly && role !== 'admin';
                return (
                  <label key={p.key} title={disabled ? 'Admin role only' : ''} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    padding: '4px 8px', borderRadius: 6,
                    background: value.includes(p.key) ? '#e8edf5' : 'transparent',
                    fontSize: 12, opacity: disabled ? 0.4 : 1,
                  }}>
                    <input
                      type="checkbox" checked={value.includes(p.key)}
                      disabled={disabled}
                      onChange={() => onChange(p.key)} style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                    />
                    {p.label}
                    {p.adminOnly && <span style={{ fontSize: 9, color: '#888', marginLeft: 'auto' }}>admin</span>}
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="page-container">
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <div className="page-header">
        <div>
          <div className="page-title"><MdPeople /> User Management</div>
          <div className="page-subtitle">Manage system users and their permissions</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-success btn-sm" onClick={() => handleExport('excel')}>
            <MdFileDownload /> Export Excel
          </button>
          <button className="btn btn-danger btn-sm" onClick={() => handleExport('pdf')}>
            <MdPictureAsPdf /> Export PDF
          </button>
          <button className="btn btn-primary" onClick={() => { setForm(initialForm); setAddModal(true); }}>
            <MdAdd /> Add User
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">System Users</div>
          <span className="badge badge-primary">{users.length} users</span>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Full Name</th>
                <th>Username</th>
                <th>Email</th>
                <th>Role</th>
                <th>Permissions</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, i) => (
                <tr key={user.id}>
                  <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{i + 1}</td>
                  <td style={{ fontWeight: 500 }}>{user.full_name || '—'}</td>
                  <td><code style={{ fontSize: 12 }}>{user.username}</code></td>
                  <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{user.email || '—'}</td>
                  <td>
                    <span className={`badge role-${user.role}`} style={{ textTransform: 'capitalize' }}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    {user.role === 'admin' ? (
                      <span className="badge badge-primary">All Permissions</span>
                    ) : (
                      <>
                        <span className="badge badge-secondary">{user.permissions?.length || 0} modules</span>
                        {user.scope_location_name && (
                          <span className="badge badge-info" style={{ marginLeft: 4 }}
                                title={`Restricted to "${user.scope_location_name}" and its sub-locations`}>
                            ⌂ {user.scope_location_name}
                          </span>
                        )}
                      </>
                    )}
                  </td>
                  <td>
                    <span className={`badge ${user.active ? 'badge-success' : 'badge-secondary'}`}>
                      {user.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn btn-accent btn-icon btn-sm" title="Edit" onClick={() => setEditModal({ ...user })}>
                        <MdEdit />
                      </button>
                      <button className="btn btn-outline btn-icon btn-sm" title="Reset password" onClick={() => handleResetPassword(user)}>
                        <MdLockReset />
                      </button>
                      <button
                        className={`btn btn-icon btn-sm ${user.active ? 'btn-warning' : 'btn-success'}`}
                        title={user.active ? 'Deactivate' : 'Activate'}
                        onClick={() => handleToggleActive(user)}
                      >
                        {user.active ? <MdBlock /> : <MdCheckCircle />}
                      </button>
                      <button className="btn btn-danger btn-icon btn-sm" title="Delete" onClick={() => setDeleteModal(user)}>
                        <MdDelete />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add User Modal */}
      {addModal && (
        <div className="modal-overlay" onClick={() => setAddModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title"><MdAdd /> Add New User</div>
              <button className="btn btn-icon" onClick={() => setAddModal(false)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input className="form-control" value={form.full_name}
                    onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} placeholder="John Doe" />
                </div>
                <div className="form-group">
                  <label className="form-label">Username *</label>
                  <input className="form-control" value={form.username}
                    onChange={e => setForm(f => ({ ...f, username: e.target.value }))} placeholder="johndoe" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input className="form-control" type="email" value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="john@example.com" />
                </div>
                <div className="form-group">
                  <label className="form-label">Password *</label>
                  <input className="form-control" type="password" value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="••••••••" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <SearchableSelect className="form-control" value={form.role}
                  onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                  <option value="operator">Operator</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </SearchableSelect>
              </div>
              {form.role !== 'admin' && (
                <div className="form-group">
                  <label className="form-label">Branch scope (optional)</label>
                  <SearchableSelect
                    className="form-control"
                    value={form.scope_location_id ?? ''}
                    onChange={e => setForm(f => ({ ...f, scope_location_id: e.target.value || null }))}
                  >
                    <option value="">— Company-wide (no restriction) —</option>
                    {locations.map(l => (
                      <option key={l.id} value={l.id}>
                        {l.location_code ? `[${l.location_code}] ` : ''}{l.location_name}
                      </option>
                    ))}
                  </SearchableSelect>
                  <div style={{ fontSize: 11, color: '#777', marginTop: 4 }}>
                    Pick a branch to restrict this user to that location and its sub-locations.
                    Leave blank for company-wide access. Admins ignore this setting.
                  </div>
                </div>
              )}
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#444', cursor: 'pointer' }}>
                  <input
                    type="checkbox" checked={form.must_change_password !== false}
                    onChange={e => setForm(f => ({ ...f, must_change_password: e.target.checked }))}
                  />
                  Force user to change password on first login (recommended)
                </label>
              </div>
              <div className="form-group">
                <label className="form-label">Permissions</label>
                <PermissionChecklist
                  value={form.permissions}
                  role={form.role}
                  onChange={key => setForm(f => ({ ...f, permissions: handlePermToggle(key, f) }))}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setAddModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAdd}><MdSave /> Create User</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editModal && (
        <div className="modal-overlay" onClick={() => setEditModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title"><MdEdit /> Edit User: {editModal.username}</div>
              <button className="btn btn-icon" onClick={() => setEditModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input className="form-control" value={editModal.full_name || ''}
                    onChange={e => setEditModal(m => ({ ...m, full_name: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input className="form-control" type="email" value={editModal.email || ''}
                    onChange={e => setEditModal(m => ({ ...m, email: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">New Password (leave blank to keep)</label>
                  <input className="form-control" type="password"
                    onChange={e => setEditModal(m => ({ ...m, password: e.target.value }))}
                    placeholder="New password..." />
                </div>
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <SearchableSelect className="form-control" value={editModal.role}
                    onChange={e => setEditModal(m => ({ ...m, role: e.target.value }))}>
                    <option value="operator">Operator</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </SearchableSelect>
                </div>
              </div>
              {editModal.role !== 'admin' && (
                <div className="form-group">
                  <label className="form-label">Branch scope</label>
                  <SearchableSelect
                    className="form-control"
                    value={editModal.scope_location_id ?? ''}
                    onChange={e => setEditModal(m => ({ ...m, scope_location_id: e.target.value || null }))}
                  >
                    <option value="">— Company-wide (no restriction) —</option>
                    {locations.map(l => (
                      <option key={l.id} value={l.id}>
                        {l.location_code ? `[${l.location_code}] ` : ''}{l.location_name}
                      </option>
                    ))}
                  </SearchableSelect>
                  <div style={{ fontSize: 11, color: '#777', marginTop: 4 }}>
                    Restrict this user to a branch (location + sub-locations). Changing this
                    revokes their current session — they'll be logged out and must sign in again.
                  </div>
                </div>
              )}
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#444', cursor: 'pointer' }}>
                  <input
                    type="checkbox" checked={!!editModal.must_change_password}
                    onChange={e => setEditModal(m => ({ ...m, must_change_password: e.target.checked }))}
                  />
                  Force user to change password on next login
                </label>
              </div>
              <div className="form-group">
                <label className="form-label">Permissions</label>
                <PermissionChecklist
                  value={editModal.permissions || []}
                  role={editModal.role}
                  onChange={key => setEditModal(m => ({
                    ...m,
                    permissions: (m.permissions || []).includes(key)
                      ? (m.permissions || []).filter(p => p !== key)
                      : [...(m.permissions || []), key]
                  }))}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setEditModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleEdit}><MdSave /> Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Reset password result modal — shows the generated temp password once */}
      {resetResult && (
        <div className="modal-overlay" onClick={() => setResetResult(null)}>
          <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title"><MdLockReset /> Temporary Password</div>
              <button className="btn btn-icon" onClick={() => setResetResult(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p style={{ marginTop: 0, color: '#444' }}>
                Share this temporary password with <strong>{resetResult.username}</strong>. This is the only time you'll see it.
              </p>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: 12,
                background: '#f8f9fb', border: '1px dashed #aab', borderRadius: 8, marginBottom: 8,
              }}>
                <code style={{ flex: 1, fontFamily: 'monospace', fontSize: 16, color: '#1a3c6e', wordBreak: 'break-all' }}>
                  {resetResult.temporary_password}
                </code>
                <button
                  type="button" className="btn btn-outline btn-sm"
                  onClick={() => {
                    navigator.clipboard?.writeText(resetResult.temporary_password);
                    showToast('Copied to clipboard');
                  }}
                  title="Copy"
                ><MdContentCopy /></button>
              </div>
              <div style={{ fontSize: 12, color: '#666' }}>
                The user will be required to change this password the first time they log in.
              </div>
              {/* Email-delivery status — only present when the admin asked for
                  it. Surfaces success or the specific reason it didn't go out
                  (most often: SMTP not configured) so the admin knows
                  whether they still need to hand off the password manually. */}
              {resetResult.email && (
                <div style={{
                  marginTop: 10, padding: 10, borderRadius: 6, fontSize: 12,
                  background: resetResult.email.sent ? '#e8f5e9' : '#fff3e0',
                  border: `1px solid ${resetResult.email.sent ? '#a5d6a7' : '#ffcc80'}`,
                  color: resetResult.email.sent ? '#2e7d32' : '#ef6c00',
                }}>
                  {resetResult.email.sent
                    ? <>✓ Emailed to <strong>{resetResult.user_email}</strong>. They have it in their inbox.</>
                    : <>⚠ Email not sent: {resetResult.email.reason}. Hand the password over manually.</>}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setResetResult(null)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal && (
        <div className="modal-overlay" onClick={() => setDeleteModal(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title" style={{ color: 'var(--danger)' }}>⚠ Delete User</div>
              <button className="btn btn-icon" onClick={() => setDeleteModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p className="confirm-msg">
                Delete user <span className="confirm-value">"{deleteModal.username}"</span>? This cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete}><MdDelete /> Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
